import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  Grid,
  Card,
  CardActionArea,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  Settings as SettingsIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Email as MessageIcon,
  Dashboard as DashboardIcon,
  PictureInPictureAlt as PopoutIcon,
  KeyboardArrowRight as ChevronRight,
} from '@mui/icons-material';

const STORAGE_KEY = 'doctoralia_settings_v3';

type DoctoraliaSettings = {
  panelUrl: string;
};

const DEFAULT_PANEL_URL = 'https://docplanner.doctoralia.com.br/';

function normalizePanelUrl(panelUrl: string): string {
  const t = (panelUrl || '').trim();
  if (!t) return DEFAULT_PANEL_URL;
  const legacy = /^https?:\/\/panel\.docplanner\.com\/?$/i;
  if (legacy.test(t)) return DEFAULT_PANEL_URL;
  return t.endsWith('/') ? t : `${t}/`;
}

const DEFAULT_SETTINGS: DoctoraliaSettings = {
  panelUrl: DEFAULT_PANEL_URL,
};

function loadSettings(): DoctoraliaSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<DoctoraliaSettings>;
      const panelUrl = normalizePanelUrl(parsed.panelUrl || '');
      const next = { panelUrl };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch { /* ignore */ }
      return next;
    }
    const legacyRaw = localStorage.getItem('doctoralia_settings_v2');
    if (legacyRaw) {
      const parsed = JSON.parse(legacyRaw) as Partial<DoctoraliaSettings>;
      const panelUrl = normalizePanelUrl(parsed.panelUrl || '');
      const next = { panelUrl };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch { /* ignore */ }
      return next;
    }
  } catch { /* ignore */ }
  return DEFAULT_SETTINGS;
}

const DoctoraliaIcon: React.FC<{ size?: number }> = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="40" height="40" rx="8" fill="#0C6FC1" />
    <path d="M20 8C13.373 8 8 13.373 8 20s5.373 12 12 12 12-5.373 12-12S26.627 8 20 8zm0 4a8 8 0 0 1 8 8 8 8 0 0 1-8 8 8 8 0 0 1-8-8 8 8 0 0 1 8-8zm-1 3v5.586l-3.707 3.707 1.414 1.414L20 22.414l3.293 3.293 1.414-1.414L21 20.586V15h-2z" fill="white" />
  </svg>
);

const popoutRefs: Record<string, Window | null> = {};
const openPopout = (key: string, url: string) => {
  const existing = popoutRefs[key];
  if (existing && !existing.closed) {
    try {
      existing.focus();
      existing.location.href = url;
      return existing;
    } catch {
      /* ignore */
    }
  }
  const w = window.screen?.availWidth || 1440;
  const h = window.screen?.availHeight || 900;
  const width = Math.min(1400, Math.round(w * 0.85));
  const height = Math.min(900, Math.round(h * 0.9));
  const left = Math.max(0, Math.round((w - width) / 2));
  const top = Math.max(0, Math.round((h - height) / 2));
  const features = `popup=yes,width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,toolbar=no,menubar=no,location=yes,status=no`;
  const win = window.open(url, key, features);
  popoutRefs[key] = win;
  return win;
};

const Doctoralia: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [settings, setSettings] = useState<DoctoraliaSettings>(loadSettings);
  const [draft, setDraft] = useState<DoctoraliaSettings>(settings);
  const [openMode, setOpenMode] = useState<'popout' | 'newtab'>('popout');
  const [configOpen, setConfigOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const urls = useMemo(() => {
    const panelBase = normalizePanelUrl(settings.panelUrl).replace(/\/$/, '');
    return {
      panel: panelBase,
      messages: `${panelBase}/#/messenger`,
    };
  }, [settings.panelUrl]);

  const persistSettings = (s: DoctoraliaSettings) => {
    setSettings(s);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  };

  const openTarget = useCallback(
    (key: string, url: string) => {
      if (openMode === 'popout') {
        const w = openPopout(key, url);
        if (!w) {
          setSnackbar({
            open: true,
            message: 'Permita popups deste site para abrir o Doctoralia.',
          });
        }
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    },
    [openMode],
  );

  const handleSaveSettings = () => {
    const next: DoctoraliaSettings = {
      panelUrl: normalizePanelUrl(draft.panelUrl),
    };
    persistSettings(next);
    setDraft(next);
    setConfigOpen(false);
    setSnackbar({ open: true, message: 'URL do painel salva.' });
  };

  const shortcuts = [
    {
      key: 'panel',
      label: 'Painel da clínica',
      desc: 'Docplanner Doctoralia — gestão da clínica',
      icon: <DashboardIcon />,
      url: urls.panel,
      color: '#0C6FC1',
    },
    {
      key: 'messages',
      label: 'Mensagens',
      desc: 'Messenger do Docplanner',
      icon: <MessageIcon />,
      url: urls.messages,
      color: '#22c55e',
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: { xs: 1.5, md: 2 },
          py: 1,
          borderRadius: 3,
          mb: 2,
          background: 'linear-gradient(135deg, #0C6FC1 0%, #0a5da0 100%)',
          border: '1px solid rgba(12,111,193,0.3)',
          boxShadow: '0 4px 24px rgba(12,111,193,0.18)',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
          <DoctoraliaIcon size={isMobile ? 28 : 34} />
          {!isMobile && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#fff', lineHeight: 1.1, fontSize: '1rem' }}>
                Doctoralia
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.7rem' }}>
                Painel e mensagens
              </Typography>
            </Box>
          )}
        </Box>

        <ToggleButtonGroup
          value={openMode}
          exclusive
          size="small"
          onChange={(_, v) => v && setOpenMode(v)}
          sx={{
            '& .MuiToggleButton-root': {
              color: 'rgba(255,255,255,0.85)',
              borderColor: 'rgba(255,255,255,0.25)',
              fontSize: '0.7rem',
              fontWeight: 600,
              py: 0.4,
              px: 1.2,
              textTransform: 'none',
              gap: 0.5,
              '&.Mui-selected': {
                bgcolor: 'rgba(255,255,255,0.95)',
                color: '#0C6FC1',
                '&:hover': { bgcolor: '#fff' },
              },
            },
          }}
        >
          <ToggleButton value="popout">
            <PopoutIcon sx={{ fontSize: 14 }} />
            Popout
          </ToggleButton>
          <ToggleButton value="newtab">
            <OpenInNewIcon sx={{ fontSize: 14 }} />
            Nova aba
          </ToggleButton>
        </ToggleButtonGroup>

        <Tooltip title="URL do painel">
          <IconButton
            size="small"
            onClick={() => {
              setDraft(settings);
              setConfigOpen(true);
            }}
            sx={{ color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
          >
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>

      <Grid container spacing={2} sx={{ flex: 1, minHeight: 0, alignContent: 'flex-start' }}>
        {shortcuts.map((s) => (
          <Grid item xs={12} md={6} key={s.key}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                minHeight: { xs: 140, md: 160 },
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: s.color,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 12px 28px ${s.color}28`,
                },
              }}
            >
              <CardActionArea
                onClick={() => openTarget(s.key, s.url)}
                sx={{
                  height: '100%',
                  p: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  justifyContent: 'flex-start',
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    bgcolor: `${s.color}15`,
                    color: s.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {React.cloneElement(s.icon as React.ReactElement, { sx: { fontSize: 28 } })}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', mb: 0.5 }}>
                    {s.label}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.4 }}>
                    {s.desc}
                  </Typography>
                </Box>
                <ChevronRight sx={{ color: '#94a3b8', flexShrink: 0, fontSize: 28 }} />
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={configOpen} onClose={() => setConfigOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            background: 'linear-gradient(135deg, #0C6FC1 0%, #0a5da0 100%)',
            color: '#fff',
            py: 2,
          }}
        >
          <DoctoraliaIcon size={28} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff', lineHeight: 1.1 }}>
              URL do painel
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)' }}>
              Base usada para abrir o painel e mensagens
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setConfigOpen(false)} sx={{ color: '#fff' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <TextField
            fullWidth
            value={draft.panelUrl}
            onChange={(e) => setDraft({ panelUrl: e.target.value })}
            placeholder="https://docplanner.doctoralia.com.br/"
            variant="outlined"
            size="small"
            label="Painel Docplanner (Brasil)"
            helperText="Mensagens abrem em …/#/messenger a partir desta URL."
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setConfigOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none', borderColor: '#e2e8f0', color: '#64748b' }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveSettings}
            variant="contained"
            startIcon={<CheckIcon />}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#0C6FC1',
              '&:hover': { bgcolor: '#0a5da0' },
            }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert icon={<CheckIcon fontSize="inherit" />} severity="success" sx={{ fontWeight: 600, borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Doctoralia;
